import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import * as moment from 'moment';
import { BaseControl } from '../../../shared/toolbar/controls/base-control';
import { ToolbarControlContext, TOOLBAR_CONTROL_CONTEXT } from '../../../shared/contracts/toolbar-control.tokens';
import { ToolbarDrawerService } from '../../../shared/toolbar/toolbar-drawer.service';
import { ApiService } from 'src/app/services/api.service';
import { actionCredits } from 'src/app/services/operators/vesselfinder-credit-actions';
import { PopupService } from 'src/app/services/popup.service';
import { CollectionPopupComponent } from 'src/app/components/shared';
import { VesselFinderMapController, VesselFinderVesselRow } from '../../vesselfinder-map.controller';
import {
  applyVesselFinderCreditSpend,
  hasVesselFinderToiFinished,
  isVesselFinderToiNotStarted,
  VESSEL_FINDER_TOI_ENDED_MESSAGE,
  VESSEL_FINDER_TOI_NOT_STARTED_MESSAGE,
  vesselFinderCreditsRemaining,
  vesselFinderSpendBlocker,
} from '../../vf-credits.model';
import { isValidImo, isValidMmsi } from '../../vessel-identifier.model';
import { VF_DEFAULT_INTERVAL_MINUTES, VF_DEFAULT_LIMIT, vfTodayUtc } from './vf-form-defaults';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Full UTC timestamp so arrival and departure are unambiguous to the user. */
export function vfDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}, ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/** One berth event as the drawer renders it — the flat row the backend returns. */
export interface VfPortCallRow {
  mmsi: string | null;
  imo: string | null;
  portName: string;
  portCountry: string | null;
  locode: string | null;
  arrival: string | null;
  departure: string | null;
  event: string | null;
}

/**
 * VesselFinder's Port Calls control — a filter form over `/portcalls`, built
 * to the same shape as `VfExpectedArrivalsControlComponent` (`ea-field`
 * labels with their own info tooltips, `sp-error`, one paid primary button,
 * a back-and-forth between a form view and a results view).
 *
 * It used to be subject-driven: the drawer followed whichever hull was active
 * on the map and pulled that one vessel's history, with the vessel card's own
 * anchor button as a second door to the same spend. Both are gone. The form
 * carries real filters — event, anchorage, a date window, a row limit — and
 * none of that fits a card button or a single active hull, so the drawer is
 * now the only place port calls are bought and the form is the only thing
 * that says what to buy.
 *
 * ONE HULL PER QUERY. The vendor endpoint does accept lists of MMSI and IMO,
 * and this drawer used to pass them through. It no longer does: a search names
 * a single vessel — one MMSI or one IMO, never several and never both. Port
 * calls bill per ROW, so `limit` is a budget shared across whatever was asked
 * about; spread over several hulls it buys a thinner history for each, which
 * is the opposite of what someone opening a vessel's berth history wants. The
 * server enforces the same rule.
 *
 * WHERE IT DIFFERS FROM EXPECTED ARRIVALS, because the data does: a result is
 * a berth EVENT, not a vessel, so rows do not center the map or open a popup
 * — there is nothing at a port call's coordinates to select. Rows are one flat
 * list, newest first, because they all belong to the one hull that was named.
 *
 * `locode` is deliberately absent: the vendor forbids combining a port LOCODE
 * with vessel identifiers in one request, and this drawer is the vessel-scoped
 * half. `imo_only` and `last_id` are likewise not exposed — the first narrows a
 * broad pull to hulls that have an IMO, which says nothing when the query names
 * its vessels outright, and the second is a paging cursor with no paging UI to
 * drive it.
 */
@Component({
  selector: 'app-vf-port-call-control',
  template: `
    <button #trigger type="button" class="tb-btn"
            [class.tb-btn--active]="drawer.isOpen(panel)"
            (click)="onToggleDrawer(trigger)"
            aria-label="Port calls"
            [matTooltip]="'Port calls · ' + costLabel()"
            matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">anchor</mat-icon>
    </button>

    <ng-template #panel>
      <app-toolbar-drawer title="Port calls" [subtitle]="costLabel()" (close)="onCloseDrawer()">
        <div class="vf-pc-panel">

          <!-- ══ Results View ══ -->
          <ng-container *ngIf="showingResults && rows.length">
            <div class="vf-pc-bar">
              <button type="button" class="ea-nav-btn" (click)="backToSearch()" matTooltip="Return to search form">
                <mat-icon class="material-icons-outlined">arrow_back</mat-icon>
                <span>Back</span>
              </button>
            </div>

            <div class="sp-sl">
              Port calls
              <span class="sp-sl__n">{{ totalRows }}</span>
              <span class="sp-sl__sub" *ngIf="subjectLabel">{{ subjectLabel }}</span>
            </div>

            <div class="vf-pc-scroll">
              <div class="vf-pc-row" *ngFor="let r of rows">
                <div class="vf-pc-row__port">
                  {{ r.portName }}<span *ngIf="r.portCountry">, {{ r.portCountry }}</span>
                  <span *ngIf="r.locode"> ({{ r.locode }})</span>
                </div>
                <div class="vf-pc-row__dates">{{ eventLine(r) }}</div>
              </div>
            </div>
          </ng-container>

          <!-- ══ Search Form View ══ -->
          <ng-container *ngIf="!showingResults || !rows.length">
            <button type="button" class="ea-results-link" *ngIf="rows.length" (click)="showActiveResults()">
              <mat-icon class="material-icons-outlined">view_list</mat-icon>
              <span>View results ({{ totalRows }})</span>
              <mat-icon class="material-icons-outlined">chevron_right</mat-icon>
            </button>

            <div class="sp-box">
              <mat-icon class="material-icons-outlined sp-box__mag">search</mat-icon>
              <input #box
                     class="sp-box__input"
                     type="text"
                     placeholder="MMSI (9 digits) or IMO (7 digits)"
                     [(ngModel)]="raw"
                     (ngModelChange)="onFieldChange()"
                     aria-label="Vessel to fetch port calls for" />
              <button type="button" class="sp-box__clear" *ngIf="raw" (click)="clearIdentifier()" aria-label="Clear identifier">
                <mat-icon>close</mat-icon>
              </button>
            </div>

            <label class="ea-field">
              <span class="ea-field__label">
                Interval (minutes)
                <span class="ea-field__req">*</span>
                <mat-icon class="material-icons-outlined ea-field__info"
                          matTooltip="The maximum age of the returned events, in minutes. Example: 43200 = the last 30 days."
                          matTooltipPosition="below"
                          (mouseenter)="$event.stopPropagation()"
                          aria-label="About interval">info</mat-icon>
              </span>
              <input class="ea-field__input" type="number" min="1" placeholder="e.g. 43200"
                     [(ngModel)]="intervalMinutes" (ngModelChange)="onFieldChange()" />
            </label>

            <div class="ea-row2">
              <label class="ea-field">
                <span class="ea-field__label">
                  From date (UTC)
                  <mat-icon class="material-icons-outlined ea-field__info"
                            matTooltip="Optional. Returns port calls between From and To — set both, or neither."
                            matTooltipPosition="below"
                            (mouseenter)="$event.stopPropagation()"
                            aria-label="About from date">info</mat-icon>
                </span>
                <input class="ea-field__input" type="date" [(ngModel)]="fromDate" (ngModelChange)="onFromDateChange()" />
              </label>
              <label class="ea-field">
                <span class="ea-field__label">
                  To date (UTC)
                  <mat-icon class="material-icons-outlined ea-field__info"
                            matTooltip="Optional. Returns port calls between From and To — set both, or neither."
                            matTooltipPosition="below"
                            (mouseenter)="$event.stopPropagation()"
                            aria-label="About to date">info</mat-icon>
                </span>
                <input class="ea-field__input" type="date" [(ngModel)]="toDate" [min]="minToDate" (ngModelChange)="onFieldChange()" />
              </label>
            </div>

            <div class="ea-row2">
              <label class="ea-field">
                <span class="ea-field__label">
                  Event
                  <mat-icon class="material-icons-outlined ea-field__info"
                            matTooltip="Which port calls to return: arrivals only, departures only, or All — both kinds."
                            matTooltipPosition="below"
                            (mouseenter)="$event.stopPropagation()"
                            aria-label="About event">info</mat-icon>
                </span>
                <select class="ea-field__input" [(ngModel)]="event" (ngModelChange)="onFieldChange()">
                  <option value="">All</option>
                  <option value="arrival">Arrival</option>
                  <option value="departure">Departure</option>
                </select>
              </label>
              <label class="ea-field">
                <span class="ea-field__label">
                  Anchorage
                  <mat-icon class="material-icons-outlined ea-field__info"
                            matTooltip="Whether anchorage calls are returned alongside berth calls: exclude them, include them (the default), or return anchorages only."
                            matTooltipPosition="below"
                            (mouseenter)="$event.stopPropagation()"
                            aria-label="About anchorage">info</mat-icon>
                </span>
                <select class="ea-field__input" [(ngModel)]="anchorage" (ngModelChange)="onFieldChange()">
                  <option [ngValue]="0">Exclude</option>
                  <option [ngValue]="1">Include</option>
                  <option [ngValue]="2">Anchorages only</option>
                </select>
              </label>
            </div>

            <label class="ea-field">
              <span class="ea-field__label">
                Limit
                <mat-icon class="material-icons-outlined ea-field__info"
                          [matTooltip]="'Maximum number of port-call rows to return, up to ' + maxLimit + '. This is what you pay for: ' + costLabel() + '. Left blank, ' + blankLimit + ' rows are requested.'"
                          matTooltipPosition="below"
                          (mouseenter)="$event.stopPropagation()"
                          aria-label="About limit">info</mat-icon>
              </span>
              <input class="ea-field__input" type="number" min="1" [max]="maxLimit" [placeholder]="'up to ' + maxLimit"
                     [(ngModel)]="limit" (ngModelChange)="onFieldChange()" />
            </label>

            <div class="sp-error" *ngIf="error">{{ error }}</div>

            <button type="button" class="tb-drawer__primary tb-drawer__primary--follows vf-cta"
                    [disabled]="loading || !canSearch() || !!searchBlocker()"
                    [matTooltip]="searchBlocker() || 'Fetch port calls for this vessel'"
                    (click)="search()">
              <mat-icon class="material-icons-outlined" [class.sp-spin]="loading">anchor</mat-icon>
              <span class="tb-drawer__primary-label">{{ loading ? 'Searching…' : 'Search port calls' }}</span>
            </button>
            <div class="tb-drawer__cost">
              <ng-container *ngIf="searchBlocker() as why; else haveCredits">{{ why }}</ng-container>
              <ng-template #haveCredits>{{ costNote }}</ng-template>
            </div>
          </ng-container>

        </div>
      </app-toolbar-drawer>
    </ng-template>
  `,
  styles: [`
    .vf-pc-panel {
      display: flex;
      flex-direction: column;
      min-width: 300px;
      max-width: 340px;
      font-size: 13px;
    }

    .ea-field { display: flex; flex-direction: column; gap: 2px; margin-bottom: 8px; }
    .ea-field__label {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 10.5px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      opacity: 0.72;
    }
    .ea-field__req { color: #ef4444; font-weight: 700; font-size: 11px; line-height: 1; }
    .ea-field__info { width: 13px; height: 13px; line-height: 13px; font-size: 13px; opacity: 0.8; cursor: help; }
    .ea-field__input {
      font: inherit;
      font-size: 12.5px;
      padding: 5px 8px;
      border: 1px solid rgba(128, 128, 128, 0.4);
      border-radius: 8px;
      background: transparent;
      color: inherit;
      outline: none;
      width: 100%;
      box-sizing: border-box;
    }
    .ea-field__input:focus { border-color: #2563EB; }
    /* A native select paints its list with the page's own colours, so the
       options are unreadable on dark unless the control itself inherits. */
    select.ea-field__input option { color: initial; }

    /* Two attempts in this file asked the browser to recolour its own calendar
       glyph -- a filter on the pseudo-element, then color-scheme -- and neither
       showed it on the dark panel. So stop asking. appearance: none drops the
       native paint entirely and the background-image below is OURS, one per
       theme, in the same muted inks the rest of this panel uses. What gets
       drawn is now what is written here, with no native rendering in the way.

       The explicit box matters: without a width and height the indicator
       collapses once its native appearance is gone, taking the click target
       with it.

       color-scheme stays, but only for the dropdown calendar it gives dark
       chrome to -- it is no longer load-bearing for the icon.

       Written with :host-context() rather than a .dark-theme descendant
       selector: the class sits on <body>, outside this component's
       emulated-encapsulation scope, where a plain descendant selector is
       never rewritten to match and the rule is simply dead. */
    .ea-field__input[type="date"]::-webkit-calendar-picker-indicator {
      -webkit-appearance: none;
      appearance: none;
      width: 14px;
      height: 14px;
      margin: 0 0 0 4px;
      padding: 0;
      cursor: pointer;
      opacity: 0.8;
      background-color: transparent;
      background-repeat: no-repeat;
      background-position: center;
      background-size: 14px 14px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='5' width='18' height='16' rx='2'/%3E%3Cline x1='16' y1='3' x2='16' y2='7'/%3E%3Cline x1='8' y1='3' x2='8' y2='7'/%3E%3Cline x1='3' y1='11' x2='21' y2='11'/%3E%3C/svg%3E");
    }
    .ea-field__input[type="date"]::-webkit-calendar-picker-indicator:hover {
      opacity: 1;
    }
    :host-context(.dark-theme) .ea-field__input[type="date"] {
      color-scheme: dark;
    }
    :host-context(.dark-theme) .ea-field__input[type="date"]::-webkit-calendar-picker-indicator {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239aa4b2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='5' width='18' height='16' rx='2'/%3E%3Cline x1='16' y1='3' x2='16' y2='7'/%3E%3Cline x1='8' y1='3' x2='8' y2='7'/%3E%3Cline x1='3' y1='11' x2='21' y2='11'/%3E%3C/svg%3E");
    }

    .ea-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }

    .sp-box {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 5px 8px;
      margin-bottom: 8px;
      border: 1px solid rgba(128, 128, 128, 0.4);
      border-radius: 8px;
    }
    .sp-box:focus-within { border-color: #2563EB; }
    .sp-box__mag { flex: 0 0 auto; font-size: 16px; width: 16px; height: 16px; line-height: 16px; opacity: 0.6; }
    .sp-box__input { flex: 1 1 auto; min-width: 0; font: inherit; font-size: 12.5px; border: none; background: transparent; color: inherit; outline: none; }
    .sp-box__clear { flex: 0 0 auto; display: inline-flex; border: none; background: transparent; color: inherit; opacity: 0.55; cursor: pointer; padding: 0; }
    .sp-box__clear:hover { opacity: 1; }
    .sp-box__clear .mat-icon { font-size: 15px; width: 15px; height: 15px; line-height: 15px; }


    .ea-results-link {
      display: flex;
      align-items: center;
      gap: 6px;
      width: 100%;
      margin-bottom: 8px;
      padding: 6px 8px;
      border: 1px solid rgba(37, 99, 235, 0.35);
      border-radius: 6px;
      background: rgba(37, 99, 235, 0.06);
      color: #2563EB;
      font: inherit;
      font-size: 11.5px;
      font-weight: 600;
      cursor: pointer;
    }
    .ea-results-link span { flex: 1 1 auto; text-align: left; }
    .ea-results-link .mat-icon { font-size: 15px; width: 15px; height: 15px; line-height: 15px; }

    .vf-pc-bar {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-bottom: 6px;
      margin-bottom: 8px;
      border-bottom: 1px solid rgba(128, 128, 128, 0.2);
    }

    .sp-sl {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 10.5px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      opacity: 0.72;
      margin-bottom: 4px;
    }
    .sp-sl__n {
      font-size: 10px;
      padding: 1px 6px;
      border-radius: 999px;
      border: 1px solid rgba(128, 128, 128, 0.4);
    }
    .sp-sl__sub { text-transform: none; letter-spacing: 0; opacity: 0.8; }

    .vf-pc-scroll { max-height: 300px; overflow-y: auto; }

    .vf-pc-row { border-top: 1px solid rgba(128, 128, 128, 0.2); padding-top: 8px; margin-top: 8px; }
    .vf-pc-row__port { font-weight: 600; font-size: 12.5px; }
    .vf-pc-row__dates { opacity: 0.7; font-size: 11.5px; line-height: 1.45; }

    .ea-nav-btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      border: 1px solid rgba(128, 128, 128, 0.35);
      border-radius: 6px;
      background: transparent;
      color: inherit;
      font: inherit;
      font-size: 11.5px;
      font-weight: 500;
      padding: 4px 8px;
      cursor: pointer;
    }
    .ea-nav-btn:hover { background: rgba(37, 99, 235, 0.08); border-color: #2563EB; color: #2563EB; }
    .ea-nav-btn .mat-icon { font-size: 15px; width: 15px; height: 15px; line-height: 15px; }

    .sp-error {
      padding: 7px 8px;
      margin-bottom: 8px;
      border-radius: 6px;
      background: rgba(217, 119, 6, 0.12);
      font-size: 11px;
      line-height: 1.45;
    }


    /*
     * These two CTAs dropped their cost badge — the price is on the drawer
     * subtitle and spelled out in the cost line below, so a third copy on the
     * button was noise. With the badge gone, the drawer's shared rule for
     * .tb-drawer__primary-label (flex: 1; text-align: left) would strand the
     * label hard left against a lone icon, so icon and label are centred as a
     * pair instead.
     *
     * Scoped to .vf-cta, and doubled on .tb-drawer__primary deliberately: the
     * shared rule carries three classes, which ties on specificity with a
     * plainer local selector and would then be decided by stylesheet order.
     * Other drawers still carry their cost badge and keep the left-aligned
     * original.
     *
     * NOTE: no backticks in here — this comment lives inside the styles
     * template literal, and one would close the string.
     */
    .tb-drawer__primary.vf-cta { justify-content: center; }
    .tb-drawer__primary.vf-cta .tb-drawer__primary-label { flex: 0 0 auto; text-align: center; }

    .sp-spin { animation: sp-spin 1.1s linear infinite; }
    @keyframes sp-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) { .sp-spin { animation: none; } }
  `],
})
export class VfPortCallControlComponent extends BaseControl implements OnDestroy {
  @ViewChild('panel') panel!: TemplateRef<any>;
  @ViewChild('trigger') triggerRef!: ElementRef;

  /**
   * One box for both kinds, exactly as `VfSearchControlComponent` does it:
   * a 9-digit token is an MMSI and a 7-digit token is an IMO, so asking the
   * user which field to type in is asking them to restate what the number
   * already says. `_identifier()` decides which of the two it was, and only
   * that one value is ever sent.
   */
  raw = '';
  intervalMinutes: number | null = VF_DEFAULT_INTERVAL_MINUTES;
  fromDate = vfTodayUtc();
  toDate = vfTodayUtc();
  event: '' | 'arrival' | 'departure' = '';
  /** The vendor's own default — anchorage calls included alongside berths. */
  anchorage: 0 | 1 | 2 = 1;
  limit: number | null = VF_DEFAULT_LIMIT;

  loading = false;
  error: string | null = null;
  rows: VfPortCallRow[] = [];
  /** "MMSI 636024674" — whose berths these are, printed beside the row count. */
  subjectLabel = '';
  showingResults = false;

  /** `vesselFinderPortCallMaxLimit` — the ceiling the server clamps to. */
  readonly maxLimit = 100;
  /**
   * `vesselFinderPortCallLimit` — what the SERVER requests when the field is
   * left blank, which is not `VF_DEFAULT_LIMIT` (what the field opens on). The
   * two differ on purpose: clearing the box asks for the server's answer, and
   * at 2 credits a row that answer is five times the price, so the tooltip and
   * the cost line below both name it.
   */
  readonly blankLimit = 50;

  private readonly _reset = (): void => {
    this.clear();
  };

  /** The identifier this control put in the box itself — never one the user typed. */
  private _autofilled: string | null = null;

  /**
   * A hull became active on the map. Offer it, but never at the cost of work
   * already done: the box is only rewritten when it is empty or still holds
   * the last thing this handler put there. Type your own identifier and the
   * map stops interfering with it until you clear the box.
   */
  private readonly _activeVessel = (row: VesselFinderVesselRow | null): void => {
    const id = row ? String(row.mmsi ?? row.imo ?? '') : '';
    if (!id) return;
    const current = this.raw.trim();
    if (current && current !== this._autofilled) return;
    this.raw = id;
    this._autofilled = id;
    this.onFieldChange();
  };

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    private readonly api: ApiService,
    public vfCtrl: VesselFinderMapController,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
    private readonly popupService: PopupService,
  ) {
    super();
    // A task/project switch clears the controller, and these results belong to
    // the task they were bought under — the drawer must not show the previous
    // one's berths against a sensor that never paid for them.
    this.vfCtrl.onResetPortCalls = this._reset;
    this.vfCtrl.onActiveVesselChange = this._activeVessel;
  }

  private get sensor(): any {
    return this.context.options?.['sensor'];
  }

  /** Back to the opening state — the shipped defaults, not an empty form. */
  clear(): void {
    this.raw = '';
    this._autofilled = null;
    this.intervalMinutes = VF_DEFAULT_INTERVAL_MINUTES;
    this.fromDate = vfTodayUtc();
    this.toDate = vfTodayUtc();
    this.event = '';
    this.anchorage = 1;
    this.limit = VF_DEFAULT_LIMIT;
    this.error = null;
    this.rows = [];
    this.subjectLabel = '';
    this.showingResults = false;
  }

  get totalRows(): number {
    return this.rows.length;
  }

  costLabel(): string {
    const c = actionCredits(this.sensor, 'portCalls');
    return `${c} ${c === 1 ? 'credit' : 'credits'} (per port)`;
  }

  /** Rows this search will ask for — the typed limit, clamped, or the server's blank fallback. */
  private get requestedRows(): number {
    return this.limit != null ? Math.min(this.limit, this.maxLimit) : this.blankLimit;
  }

  /** The ceiling this search could cost — `limit` is the only dial on a per-row price. */
  get worstCaseLabel(): string {
    const total = this.requestedRows * (actionCredits(this.sensor, 'portCalls') || 0);
    return `${total} ${total === 1 ? 'credit' : 'credits'}`;
  }

  /**
   * The cost line under the button.
   *
   * Built here rather than spliced together in the template, because
   * `costLabel()` carries its "(per port)" in parentheses — it has to stand
   * alone as the drawer subtitle and as the button's badge — and dropped into
   * a sentence that read "Uses 2 credits (per port) returned", which parses as
   * nothing at all. Spelled out as a sentence instead, with the ceiling tied to
   * the row count that produced it so the number is accountable.
   */
  get costNote(): string {
    const unit = actionCredits(this.sensor, 'portCalls') || 0;
    const rows = this.requestedRows;
    return (
      `${unit} ${unit === 1 ? 'credit' : 'credits'} per port returned — ` +
      `up to ${this.worstCaseLabel} for ${rows} ${rows === 1 ? 'row' : 'rows'}.`
    );
  }

  /** Null while the spend may proceed, a human-readable refusal otherwise. */
  searchBlocker(): string | null {
    return vesselFinderSpendBlocker(this.sensor, 'portCalls');
  }

  onToggleDrawer(trigger: any): void {
    this.drawer.toggle(this.panel, this.vcr, trigger);
  }

  onCloseDrawer(): void {
    this.error = null;
    this.drawer.close();
  }

  backToSearch(): void {
    this.showingResults = false;
  }

  showActiveResults(): void {
    if (this.rows.length) this.showingResults = true;
  }

  get minToDate(): string | null {
    return this.fromDate ? this.fromDate : null;
  }

  onFromDateChange(): void {
    if (this.fromDate && this.toDate && this.toDate < this.fromDate) this.toDate = '';
    this.onFieldChange();
  }

  onFieldChange(): void {
    this.error = this._validationError();
  }

  /**
   * "Departure: 19 Aug 2026, 09:24 UTC" — the event the vendor reported, at
   * the time it reported. A row with neither stamp is a berth the vessel has
   * not left, which is a fact worth printing rather than an em dash.
   */
  eventLine(r: VfPortCallRow): string {
    const stamp = r.departure || r.arrival;
    if (!stamp) return 'Still in port';
    const label = r.event ? r.event.charAt(0).toUpperCase() + r.event.slice(1) : r.departure ? 'Departure' : 'Arrival';
    return `${label}: ${vfDateTime(stamp)}`;
  }

  canSearch(): boolean {
    if (this._validationError()) return false;
    const id = this._identifier();
    return !!(id.mmsi || id.imo) && !!this.intervalMinutes;
  }

  /**
   * The one hull this search is about.
   *
   * The box still SPLITS on the separators a paste drags in — a trailing comma
   * or a stray newline is not worth an error message — but two DIFFERENT
   * identifiers are refused outright through `extra`, rather than silently
   * dropping one and charging for the other. The same number twice is not two
   * identifiers.
   *
   * MMSI wins when both kinds are present, matching `resolveSingleIdentifier`
   * on the server so the two can never disagree about which vessel was asked
   * for.
   */
  private _identifier(): { mmsi: string | null; imo: string | null; rejected: string[]; extra: boolean } {
    const tokens = String(this.raw ?? '')
      .split(/[\s,;]+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const valid: string[] = [];
    const rejected: string[] = [];
    for (const t of tokens) (isValidMmsi(t) || isValidImo(t) ? valid : rejected).push(t);

    const unique = [...new Set(valid)];
    const mmsi = unique.find((t) => isValidMmsi(t)) ?? null;
    const imo = mmsi ? null : unique.find((t) => isValidImo(t)) ?? null;
    return { mmsi, imo, rejected, extra: unique.length > 1 };
  }

  /** The box's own ✕ — clears what was typed, never the filters set around it. */
  clearIdentifier(): void {
    this.raw = '';
    // Cleared by hand, so the map is welcome to offer the active hull again.
    this._autofilled = null;
    this.onFieldChange();
  }

  /** Every refusal the form can make on its own, in the order a user meets them. */
  private _validationError(): string | null {
    const id = this._identifier();
    if (id.rejected.length) {
      return `Not a valid MMSI/IMO: ${id.rejected.join(', ')}`;
    }
    if (id.extra) {
      return 'Port calls are fetched one vessel at a time — enter a single MMSI or IMO.';
    }
    if (this.limit != null && (this.limit < 1 || this.limit > this.maxLimit)) {
      return `Limit must be between 1 and ${this.maxLimit}.`;
    }
    if (this.intervalMinutes != null && this.intervalMinutes < 1) {
      return 'Interval must be at least 1 minute.';
    }
    if (this.fromDate && this.toDate && this.toDate < this.fromDate) {
      return 'To date cannot be earlier than From date.';
    }
    if (!!this.fromDate !== !!this.toDate) {
      return 'Set both From and To date, or neither.';
    }
    return null;
  }

  /**
   * `<input type="date">` gives a bare "YYYY-MM-DD"; `/portcalls` wants
   * "YYYY-MM-DD HH:mm:ss". Same `moment` idiom as the Expected Arrivals
   * control — `.utcOffset(0, true)` relabels the picked wall-clock day as UTC
   * without shifting it, so the calendar day chosen is never moved by the
   * browser's own timezone.
   */
  private toVesselFinderUtcStamp(dateOnly: string, endOfDay: boolean): string | undefined {
    const parseMoment: any = (moment as any).default ?? (moment as any);
    const m = parseMoment(dateOnly, 'YYYY-MM-DD', true);
    if (!m.isValid()) return undefined;
    return m
      .utcOffset(0, true)
      [endOfDay ? 'endOf' : 'startOf']('day')
      .format('YYYY-MM-DD HH:mm:ss');
  }

  search(): void {
    this.error = this._validationError();
    if (this.error || this.loading) return;

    const id = this._identifier();
    if (!id.mmsi && !id.imo) {
      this.error = 'Enter an MMSI or IMO.';
      return;
    }
    if (!this.intervalMinutes) {
      this.error = 'Enter an interval in minutes.';
      return;
    }

    // Both ends of the TOI, in the order `vesselFinderSpendRefusal` asks them.
    // This form does its own worst-case credit arithmetic below rather than
    // going through that helper, so the window checks have to be repeated here
    // — a form that skipped them would be the one path to the vendor that does
    // not know when the shop is open.
    if (isVesselFinderToiNotStarted(this.sensor)) {
      this.popupService.openPopup(CollectionPopupComponent, VESSEL_FINDER_TOI_NOT_STARTED_MESSAGE, '', '40%');
      return;
    }

    if (hasVesselFinderToiFinished(this.sensor)) {
      this.popupService.openPopup(CollectionPopupComponent, VESSEL_FINDER_TOI_ENDED_MESSAGE, '', '40%');
      return;
    }

    // Port calls bill per ROW, so `limit` — not the identifier count — is what
    // sets the ceiling. Checked here as well as server-side so the refusal
    // names the dial the user can actually turn.
    const unitCost = actionCredits(this.sensor, 'portCalls') || 0;
    const requestedLimit = this.limit != null ? Math.min(this.limit, this.maxLimit) : this.blankLimit;
    const worstCaseCost = requestedLimit * unitCost;
    const remaining = vesselFinderCreditsRemaining(this.sensor);
    if (remaining != null && remaining < worstCaseCost) {
      this.popupService.openPopup(
        CollectionPopupComponent,
        `Insufficient credits to perform this action. Port calls cost ${unitCost} credits per port, ` +
          `so a limit of ${requestedLimit} needs ${worstCaseCost}. Lower the limit to fit your remaining balance.`,
        '',
        '40%',
      );
      return;
    }

    const { taskid, sensorId } = this.vfCtrl;
    if (!taskid || !sensorId) {
      this.error = 'This sensor is not attached to a task yet.';
      return;
    }

    this.loading = true;
    this.api
      .getVesselFinderPortCalls({
        taskid,
        sensorId,
        mmsi: id.mmsi ?? undefined,
        imo: id.imo ?? undefined,
        intervalMinutes: this.intervalMinutes,
        fromDate: this.fromDate ? this.toVesselFinderUtcStamp(this.fromDate, false) : undefined,
        toDate: this.toDate ? this.toVesselFinderUtcStamp(this.toDate, true) : undefined,
        event: this.event || undefined,
        anchorage: this.anchorage,
        limit: this.limit ?? undefined,
      })
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          applyVesselFinderCreditSpend(this.sensor, res, actionCredits(this.sensor, 'portCalls'));
          const data = Array.isArray(res?.data) ? res.data : [];
          this.rows = this._rows(data);
          this.subjectLabel = id.mmsi ? `MMSI ${id.mmsi}` : `IMO ${id.imo}`;
          if (!this.rows.length) {
            this.showingResults = false;
            this.error = 'No port calls on record for this vessel.';
            return;
          }
          this.showingResults = true;
        },
        error: (err: any) => {
          this.loading = false;
          if (err?.status === 402 || err?.error?.code === 'INSUFFICIENT_API_CREDITS') {
            this.popupService.openPopup(
              CollectionPopupComponent,
              err?.error?.msg || 'Insufficient credits to fetch port calls.',
              '',
              '40%',
            );
          } else {
            this.error = err?.error?.msg || 'Could not load port calls. Please try again.';
          }
        },
      });
  }

  /**
   * Vendor rows in, the drawer's rows out, newest event first.
   *
   * No grouping and no identifier fallback any more: one hull was named, so
   * every row returned is that hull's — including the ones the vendor hands
   * back carrying neither MMSI nor IMO, which the old grouping had to drop
   * because it could not tell whose they were.
   */
  private _rows(data: any[]): VfPortCallRow[] {
    const rows: VfPortCallRow[] = data.map((c) => ({
      mmsi: c?.mmsi != null ? String(c.mmsi) : null,
      imo: c?.imo != null ? String(c.imo) : null,
      portName: c?.portName || c?.locode || 'Unknown port',
      portCountry: c?.portCountry ?? null,
      locode: c?.locode ?? null,
      arrival: c?.arrivalTime ?? null,
      departure: c?.departureTime ?? null,
      event: c?.event ?? null,
    }));

    const stamp = (r: VfPortCallRow) => {
      const t = new Date(r.departure || r.arrival || 0).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    return rows.sort((a, b) => stamp(b) - stamp(a));
  }

  ngOnDestroy(): void {
    this.drawer.closeIfOpen(this.panel);
    if (this.vfCtrl.onResetPortCalls === this._reset) this.vfCtrl.onResetPortCalls = null;
    if (this.vfCtrl.onActiveVesselChange === this._activeVessel) this.vfCtrl.onActiveVesselChange = null;
  }
}
